import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import Modal from "../UI/Modal";
import Loader from "../UI/Loader";
import {
  getMentoresSeguimientoService,
  reasignarMentorService,
} from "../../services/seguimiento/seguimiento.service";

/**
 * Modal para cambiar el mentor de un estudiante.
 * Motivo obligatorio (mínimo 5 caracteres).
 *
 * @param {number} estudianteId
 * @param {string} estudianteNombre
 * @param {number} mentorActualId  — para excluirlo del selector
 * @param {string} [mentorActualNombre]
 * @param {() => void} onClose
 * @param {() => void} onSuccess
 */
const ReasignarMentorModal = ({
  estudianteId,
  estudianteNombre,
  mentorActualId,
  mentorActualNombre,
  onClose,
  onSuccess,
}) => {
  const [mentores, setMentores] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getMentoresSeguimientoService();
        const options = data
          .filter((m) => m.id !== mentorActualId)
          .map((m) => ({ value: m.id, label: m.label }));
        setMentores(options);
      } catch {
        toast.error("Error al cargar los mentores");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [mentorActualId]);

  const motivoValido = useMemo(() => motivo.trim().length >= 5, [motivo]);
  const canSubmit = selectedMentor && motivoValido && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!selectedMentor) {
      setError("Selecciona un mentor destino.");
      return;
    }
    if (!motivoValido) {
      setError("El motivo es obligatorio y debe tener al menos 5 caracteres.");
      return;
    }

    setSubmitting(true);
    try {
      await reasignarMentorService({
        estudiante_id: estudianteId,
        nuevo_mentor_id: selectedMentor.value,
        motivo: motivo.trim(),
      });
      toast.success("Mentor reasignado correctamente");
      setSuccessInfo({
        nuevoMentor: selectedMentor.label,
        estudiante: estudianteNombre,
      });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.motivo?.[0] ||
        err.response?.data?.message ||
        "No se pudo reasignar el mentor";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setSuccessInfo(null);
    onSuccess();
  };

  return (
    <Modal
      open
      onClose={successInfo ? handleCloseSuccess : onClose}
      title={successInfo ? "Reasignación exitosa" : "Cambiar mentor"}
      size="md"
    >
      {loading ? (
        <Loader center type="dots" text="Cargando mentores..." />
      ) : successInfo ? (
        <div className="px-2 px-md-3 text-center py-3">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{
              width: 72,
              height: 72,
              background: "rgba(16,185,129,0.15)",
              color: "#10b981",
            }}
          >
            <Icon icon="mdi:check-circle" width="48" />
          </div>
          <h5 className="mb-2">Mentor reasignado</h5>
          <p className="text-secondary-light mb-3">
            <strong>{successInfo.estudiante}</strong> ahora está asignado a{" "}
            <strong>{successInfo.nuevoMentor}</strong>.
          </p>
          <div className="d-flex justify-content-center mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-primary rounded-3 px-4"
              onClick={handleCloseSuccess}
              autoFocus
            >
              Continuar
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="px-2 px-md-3">
          {/* Contexto */}
          <div className="alert alert-info py-2 mb-3">
            <div className="d-block small fw-semibold">Estudiante</div>
            <div className="mb-1">{estudianteNombre}</div>
            {mentorActualNombre && (
              <>
                <div className="d-block small fw-semibold">Mentor actual</div>
                <div>{mentorActualNombre}</div>
              </>
            )}
          </div>

          {error && (
            <div className="alert alert-danger py-2 mb-3 text-sm">{error}</div>
          )}

          {/* Nuevo mentor */}
          <div className="mb-4">
            <label className="form-label fw-semibold small">
              Nuevo mentor <span className="text-danger">*</span>
            </label>
            <Select
              options={mentores}
              value={selectedMentor}
              onChange={setSelectedMentor}
              placeholder="Selecciona un mentor..."
              className="react-select"
              isClearable
              noOptionsMessage={() =>
                "No hay otros mentores disponibles en el plantel"
              }
              isDisabled={submitting}
            />
            <div className="form-text mt-2">
              Se muestran mentores del mismo plantel del estudiante.
            </div>
          </div>

          {/* Motivo */}
          <div className="mb-4">
            <label className="form-label fw-semibold small">
              Motivo del cambio <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control shadow-sm rounded-3"
              rows="3"
              placeholder="Ej: Cambio por afinidad de área, disponibilidad, etc."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              disabled={submitting}
              minLength={5}
            />
            <div className="d-flex justify-content-between mt-1">
              <small
                className={motivoValido ? "text-success" : "text-secondary"}
              >
                Mínimo 5 caracteres.
              </small>
              <small className="text-secondary">{motivo.trim().length}</small>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light border rounded-3 px-4"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-3 px-4 shadow-sm"
              disabled={!canSubmit}
            >
              {submitting ? "Reasignando..." : "Reasignar"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default ReasignarMentorModal;
